import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarPlus, Copy, Check, RefreshCw, BellRing, Activity } from "lucide-react";

function timeAgo(isoStr) {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(isoStr).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

// Calendar-sync affordances (F&F): one shared content block reused by
//  - the card on /calendar and the "Calendar sync" section in settings,
//  - a dialog opened from the /calendar header button and from the subtle
//    "subscribe to all your commitments" links on opportunity/collective pages.

export function CalendarSubscribeContent({ autoReveal = false }) {
  const [url, setUrl] = useState(null);
  const [alarms, setAlarms] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [client, setClient] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const reveal = async () => {
    setBusy(true);
    try {
      const info = await api.calendarFeedInfo();
      setUrl(info.url);
      setAlarms(!!info.alarms);
      setFetchedAt(info.last_fetched_at || null);
      setClient(info.client || null);
    } catch { toast.error("Could not fetch your calendar link"); }
    finally { setBusy(false); }
  };

  React.useEffect(() => { if (autoReveal) reveal(); }, [autoReveal]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; runs once on mount

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste it into your calendar app");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — select and copy the link manually"); }
  };

  const toggleAlarms = async (on) => {
    setAlarms(on); // optimistic
    try {
      await api.setCalendarFeedPrefs(on);
      toast.success(on ? "Reminders on — events now carry a 24h alert" : "Reminders off");
    } catch {
      setAlarms(!on);
      toast.error("Could not update the reminder setting");
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      setUrl((await api.regenerateCalendarFeed()).url);
      setFetchedAt(null);
      setClient(null);
      setConfirming(false);
      toast.success("New link created — the old one no longer works");
    } catch { toast.error("Could not regenerate the link"); }
    finally { setBusy(false); }
  };

  if (!url) {
    return (
      <div className="mt-3">
        <Button data-testid="calendar-subscribe-reveal" onClick={reveal} disabled={busy}
          className="rounded-full bg-forest hover:bg-forest-hover">
          {busy ? "One sec…" : "Show my personal link"}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex gap-2">
        <Input data-testid="calendar-feed-url" readOnly value={url} onFocus={(e) => e.target.select()}
          className="h-10 font-mono text-xs" />
        <Button data-testid="calendar-feed-copy" onClick={copy} variant="outline" className="rounded-full h-10 shrink-0">
          {copied ? <Check className="w-4 h-4 text-forest" /> : <Copy className="w-4 h-4" />}
          <span className="ml-1.5 hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
        </Button>
      </div>
      <p className="text-[11px] text-neutral-500 mt-2">
        This link is private to you — anyone with it can see your commitments. Don't share it.
      </p>
      <p data-testid="calendar-feed-health" className="text-[11px] mt-1.5 inline-flex items-center gap-1.5">
        <Activity className={`w-3 h-3 ${fetchedAt ? "text-forest" : "text-neutral-400"}`} />
        {fetchedAt ? (
          <span className="text-neutral-600">
            Last fetched by {client || "your calendar app"}: <span className="font-semibold text-forest">{timeAgo(fetchedAt)}</span>
          </span>
        ) : (
          <span className="text-neutral-500">Not fetched yet — it can take your calendar app a few hours to first sync.</span>
        )}
      </p>

      <div className="flex items-center justify-between gap-4 mt-4 rounded-lg bg-neutral-50 border border-neutral-100 p-3">
        <div className="text-xs text-neutral-700 inline-flex items-center gap-2">
          <BellRing className="w-3.5 h-3.5 text-terracotta" />
          <span><span className="font-semibold text-forest">Include 24h reminders</span> — your calendar alerts you the day before each event</span>
        </div>
        <Switch data-testid="calendar-alarms-toggle" checked={alarms} onCheckedChange={toggleAlarms} />
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-4 text-xs text-neutral-600">
        <div className="rounded-lg bg-neutral-50 border border-neutral-100 p-3">
          <div className="font-semibold text-forest mb-1">Google Calendar</div>
          Settings → Add calendar → <span className="font-medium">From URL</span> → paste the link.
        </div>
        <div className="rounded-lg bg-neutral-50 border border-neutral-100 p-3">
          <div className="font-semibold text-forest mb-1">Apple Calendar</div>
          iPhone: Calendar → Add Calendar → <span className="font-medium">Add Subscription Calendar</span>. Mac: File → New Calendar Subscription.
        </div>
        <div className="rounded-lg bg-neutral-50 border border-neutral-100 p-3">
          <div className="font-semibold text-forest mb-1">Outlook</div>
          Add calendar → <span className="font-medium">Subscribe from web</span> → paste the link.
        </div>
      </div>

      <div className="mt-4">
        {!confirming ? (
          <button data-testid="calendar-feed-regenerate" onClick={() => setConfirming(true)}
            className="text-xs text-neutral-500 hover:text-red-600 underline underline-offset-2 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Regenerate link
          </button>
        ) : (
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <span className="text-red-600">The current link stops working immediately — you'll need to re-subscribe in your calendar apps.</span>
            <Button data-testid="calendar-feed-regenerate-confirm" size="sm" onClick={regenerate} disabled={busy}
              className="rounded-full h-7 text-xs bg-red-600 hover:bg-red-700 text-white">Regenerate</Button>
            <button onClick={() => setConfirming(false)} className="text-neutral-500 underline underline-offset-2">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

const BLURB = "Everything you sign up for — applications, RSVPs, help you're giving, events you host — lands in Google Calendar, Apple Calendar or Outlook automatically and stays in sync.";

export function CalendarSubscribe({ className = "mt-8" }) {
  return (
    <div data-testid="calendar-subscribe" className={`bg-white border border-neutral-200 rounded-xl p-6 ${className}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
        <CalendarPlus className="w-3.5 h-3.5 text-terracotta" /> Subscribe in your calendar app
      </div>
      <p className="text-sm text-neutral-600 mt-2 max-w-xl">{BLURB}</p>
      <CalendarSubscribeContent />
    </div>
  );
}

export function CalendarSubscribeDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="calendar-subscribe-dialog" className="max-w-lg max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-terracotta" /> Subscribe in your calendar app
          </DialogTitle>
          <DialogDescription>{BLURB}</DialogDescription>
        </DialogHeader>
        {open && <CalendarSubscribeContent autoReveal />}
      </DialogContent>
    </Dialog>
  );
}

/** Subtle inline entry point for detail pages. */
export function CalendarSubscribeLink({ label = "or subscribe to all your commitments", testId = "calendar-subscribe-link" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={testId} onClick={() => setOpen(true)}
        className="text-[11px] text-neutral-500 hover:text-forest underline underline-offset-2 inline-flex items-center gap-1">
        <CalendarPlus className="w-3 h-3" /> {label}
      </button>
      <CalendarSubscribeDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
